<script setup>
import { ref, computed, watch } from 'vue';
import { router, useForm, usePage } from '@inertiajs/vue3';
import ActionSection from '@/Components/ActionSection.vue';
import ConfirmsPassword from '@/Components/ConfirmsPassword.vue';
import DangerButton from '@/Components/DangerButton.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import TextInput from '@/Components/TextInput.vue';

const props = defineProps({
    requiresConfirmation: Boolean,
});

const page = usePage();
const enabling = ref(false);
const confirming = ref(false);
const disabling = ref(false);
const regeneratingCodes = ref(false);
const fetchingCodes = ref(false);
const qrCode = ref(null);
const setupKey = ref(null);
const recoveryCodes = ref([]);

const confirmationForm = useForm({
    code: '',
});

const twoFactorEnabled = computed(
    () => ! enabling.value && page.props.auth.user?.two_factor_enabled,
);
// const twoFactorEnabled = computed(
//     () => true,
// );

watch(twoFactorEnabled, () => {
    if (! twoFactorEnabled.value) {
        confirmationForm.reset();
        confirmationForm.clearErrors();
    }
});

const enableTwoFactorAuthentication = () => {
    enabling.value = true;

    router.post(route('two-factor.enable'), {}, {
        preserveScroll: true,
        onSuccess: () => Promise.all([
            showQrCode(),
            showSetupKey(),
            showRecoveryCodes(),
        ]),
        onFinish: () => {
            enabling.value = false;
            confirming.value = props.requiresConfirmation;
        },
    });
};

const showQrCode = () => {
    return axios.get(route('two-factor.qr-code')).then(response => {
        qrCode.value = response.data.svg;
    });
};

const showSetupKey = () => {
    return axios.get(route('two-factor.secret-key')).then(response => {
        setupKey.value = response.data.secretKey;
    });
}

const showRecoveryCodes = () => {
    fetchingCodes.value = true;
    return axios.get(route('two-factor.recovery-codes')).then(response => {
        recoveryCodes.value = response.data;
    }).finally(() => {
        fetchingCodes.value = false;
    });
};

const confirmTwoFactorAuthentication = () => {
    confirmationForm.post(route('two-factor.confirm'), {
        errorBag: "confirmTwoFactorAuthentication",
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            confirming.value = false;
            qrCode.value = null;
            setupKey.value = null;
        },
    });
};

const regenerateRecoveryCodes = () => {
    regeneratingCodes.value = true;
    axios
        .post(route('two-factor.recovery-codes'))
        .then(() => showRecoveryCodes())
        .finally(() => {
            regeneratingCodes.value = false;
        });
};

const disableTwoFactorAuthentication = () => {
    disabling.value = true;

    router.delete(route('two-factor.disable'), {
        preserveScroll: true,
        onSuccess: () => {
            disabling.value = false;
            confirming.value = false;
        },
    });
};

const numericRule = (value) => /^\d+$/.test(value) || 'Only numbers are allowed';
</script>

<template>
    <div>
        <v-row>
            <v-col cols="12" sm="4">
                <div class="text-h6 pb-1">Two Factor Authentication</div>
                <div class="text-body-2 text-secondary">Add additional security to your account using two factor authentication.</div>
            </v-col>
            <v-col cols="12" sm="8">
                <v-card>
                    <v-card-text class="d-flex flex-column ga-2">
                        <div class="fs-2 fw-medium">
                            <div v-if="twoFactorEnabled && ! confirming">
                                You have enabled two factor authentication.
                            </div>

                            <div v-else-if="twoFactorEnabled && confirming">
                                Finish enabling two factor authentication.
                            </div>

                            <div v-else>
                                You have not enabled two factor authentication.
                            </div>
                        </div>
                        <div class="w-35rem max-w-100">
                            <div class="text-secondary fs-n1">
                                When two factor authentication is enabled, you will be prompted for a secure, random token during authentication. You may retrieve this token from your phone's Google Authenticator application.
                            </div>
                            <div class="">
                                <div v-if="twoFactorEnabled">
                                    <div v-if="qrCode">
                                        <div class="py-2 fs-n1 fw-medium">
                                            <span v-if="confirming">
                                                To finish enabling two factor authentication, scan the following QR code using your phone's authenticator application or enter the setup key and provide the generated OTP code.
                                            </span>

                                            <span v-else>
                                                Two factor authentication is now enabled. Scan the following QR code using your phone's authenticator application or enter the setup key.
                                            </span>
                                        </div>
                                        <div class="py-2 d-flex">
                                            <div class="pa-2 bg-white border-divider border">
                                                <div class="d-block bg-surface" v-html="qrCode"></div>
                                            </div>
                                        </div>

                                        <div v-if="setupKey" class="py-2">
                                            <p class="fs-n1 fw-medium">
                                                Setup Key: <span v-html="setupKey"></span>
                                            </p>
                                        </div>

                                        <div v-if="confirming" class="py-2">
                                            <div class="w-16rem max-w-100">
                                                <v-text-field
                                                    id="code"
                                                    label="Code"
                                                    v-model="confirmationForm.code"
                                                    :rules="[numericRule]"
                                                    placeholder=""
                                                    variant="outlined"
                                                    density="comfortable"
                                                    hide-details="auto"
                                                    autofocus
                                                    :error-messages="confirmationForm.errors.code"
                                                    @keyup.enter="confirmTwoFactorAuthentication"
                                                ></v-text-field>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="recoveryCodes.length > 0 && ! confirming">
                                        <div class="py-2 fs-n1 fw-medium">
                                            Store these recovery codes in a secure password manager. They can be used to recover access to your account if your two factor authentication device is lost.
                                        </div>
                                        <div class="py-2">
                                            <v-sheet variant="tonal" color="surface-light py-4 px-5 w-28rem max-w-100">
                                                <div class="fs-2 lh-3 font-mono">
                                                    <div v-for="code in recoveryCodes" :key="code">
                                                        {{ code }}
                                                    </div>
                                                </div>
                                            </v-sheet>
                                        </div>
                                    </div>
                                </div>

                                <div class="pt-4">
                                    <div v-if="! twoFactorEnabled">
                                        <ConfirmsPassword @confirmed="enableTwoFactorAuthentication">
                                            <v-btn text="Enable" :disabled="enabling" :loading="enabling"></v-btn>
                                        </ConfirmsPassword>
                                    </div>

                                    <div v-else class="d-flex flex-wrap ga-3">
                                        <ConfirmsPassword v-if="confirming" @confirmed="confirmTwoFactorAuthentication">
                                            <v-btn  text="Confirm" :disabled="enabling" :loading="enabling"></v-btn>
                                        </ConfirmsPassword>

                                        <ConfirmsPassword v-if="recoveryCodes.length > 0 && ! confirming" @confirmed="regenerateRecoveryCodes">
                                            <v-btn  color="secondary" variant="outlined" text="Regenerate Recovery Codes" :disabled="regeneratingCodes" :loading="regeneratingCodes"></v-btn>
                                        </ConfirmsPassword>

                                        <ConfirmsPassword v-if="recoveryCodes.length === 0 && ! confirming" @confirmed="showRecoveryCodes">
                                            <v-btn  color="secondary" variant="outlined" text="Show Recovery Codes" :disabled="fetchingCodes" :loading="fetchingCodes"></v-btn>
                                        </ConfirmsPassword>

                                        <ConfirmsPassword v-if="confirming" @confirmed="disableTwoFactorAuthentication">
                                            <v-btn  color="secondary" variant="outlined" text="Cancel" :disabled="disabling" :loading="disabling"></v-btn>
                                        </ConfirmsPassword>

                                        <ConfirmsPassword v-if="!confirming" @confirmed="disableTwoFactorAuthentication">
                                            <v-btn  color="danger" text="Disable" :disabled="disabling" :loading="disabling"></v-btn>
                                        </ConfirmsPassword>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>
